import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import {
  selectBrandLoaded,
  selectIsPartner
} from 'libs/shared/utility/brand/src/lib/+state/brand.selectors';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

// Store
import { PolicyDocumentsSelectors, PolicySelectors } from '@amfam/policy/data-access';
import { PolicyUtilService } from '@amfam/policy/e-signature/data-access';
import { GoPaperLessSelectors } from '@amfam/policy/go-paperless/data-access';
import {
  NamedInsured,
  Policy,
  PolicyActions,
  PolicyAnalytics,
  PolicyDetails,
  PolicyDocument,
  PolicyRole,
  PropertyPolicy
} from '@amfam/policy/models';
import { AnalyticsFacade, PageAnalyticParams } from '@amfam/shared/analytics';
// Constants file for claims
import {
  ButtonProps,
  ClaimsNavigationPaths,
  Components,
  ComponentTheme,
  PolicyChangeRequestType,
  PolicyView,
  TypeOfPolicy
} from '@amfam/shared/models';
import { QuickLink, QuickLinksService } from '@amfam/shared/ui/quick-links';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService, ImpersonationRoles } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, ScrollService, UtilService } from '@amfam/shared/utility/shared-services';
import { Action, DockingBarService, LoadingSpinnerService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';
import { SessionActivityService } from '../../login';
import { PolicyViewService } from '../services/policy-view.service';

@Component({
  selector: 'app-plproperty-large',
  templateUrl: './plproperty-large.component.html',
  styleUrls: ['../shared/policy-shared.scss'],
  standalone: false
})
export class PlpropertyLargeComponent implements OnInit, OnDestroy {
  policy: PropertyPolicy = null;
  isEnrolledGoPaperless: boolean;
  private claimsFNOLEnabled: boolean;
  protected isConnectPartner = false;
  openGeneral = false;
  openCoverage = false;
  openDiscounts = false;
  openDocuments = false;
  public policyDocuments: Observable<PolicyDocument[]>;

  // subscriptions
  private policySub$: Observable<Policy>;
  private userEnrolledGoPaperlessSubscription$: Observable<boolean>;
  private policyErrorSub$: Observable<string>;
  private impersonateRolesServiceIsAuthorizedSub$: Observable<boolean>;

  private isAuthorized: boolean;

  private componentName: any = {};
  componentData: any;
  quickLinksOverride = new Array<QuickLink>();

  // Shared subject for completing observables
  protected stop$: Subject<void> = new Subject<void>();

  // **** Start of Analytics data for this component
  private pageAnalytic: PageAnalyticParams = {
    pageName: 'MyAccount:Policies:PolicyDetails',
    experience: '',
    primaryCategory: 'My Account',
    subCategory1: 'Policies',
    subCategory2: '',
    subCategory3: ''
  };
  showNewBadge: boolean;
  isNewTemplate = this.feature.isEnabled('property_policy_new_template');
  nameInsured: string;
  nameInsuredHeading: string;
  premiumActionButtons$: Observable<ButtonProps[]>;
  peopleCoveredActionButtons: ButtonProps[];
  coverageActionButtons: ButtonProps[];
  jumpLinks: ButtonProps[];
  // **** End of Analytics data for this component

  requestChangeNewTemplate = this.feature.isEnabled('request_change_new_template');
  policyView: PolicyView;
  /* Shared Components */
  showPolicyView = false;
  policyNumber: string;
  theme: ComponentTheme;
  sessionHandler;
  public get inWebview(): boolean {
    return this.router.url.includes('webview') && this.feature.isEnabled('policyview_webview');
  }
  public get isPolicyViewEnabled(): boolean {
    return this.feature.isEnabled('policy_view');
  }

  constructor(
    private store: Store<fromRoot.RootState>,
    private route: ActivatedRoute,
    public spinner: LoadingSpinnerService,
    private copyService: CopyService,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private analyticsFacade: AnalyticsFacade,
    private feature: FeatureFlagService,
    private quickLinksService: QuickLinksService,
    private impersonateRolesService: ImpersonateRolesService,
    private policyUtilService: PolicyUtilService,
    private breakpointObserver: BreakpointObserver,
    private scrollService: ScrollService,
    private utilService: UtilService,
    private policyViewService: PolicyViewService,
    private router: Router,
    private sessionActivityService: SessionActivityService
  ) {
    this.componentName.policy = 'policy';
    this.componentData = this.copyService.getComponentData('policyDetail');
  }

  async ngOnInit() {
    this.pageTitle.set('Policy Details');
    const policyNumber = this.getPolicyNumber();
    this.showPolicyView = await this.policyViewService.showSharedComponent(
      policyNumber,
      TypeOfPolicy.Home,
      Components.PolicyView
    );
    if (this.isPolicyViewEnabled && this.showPolicyView) {
      await this.buildPolicyView();
    } else {
      this.policyUtilService.dispatchPolicySelectedAction(this.route.params);

      this.route.queryParams.subscribe(params => {
        const scrollToElementId = params['scrollTo'];
        if (scrollToElementId) {
          setTimeout(() => {
            const isSmallScreen = this.breakpointObserver.isMatched(
              '(max-width: 480px) and (orientation: portrait)'
            );
            if (isSmallScreen) this.scrollService.scrollToElement('documentsCard', 85);
            else this.scrollService.scrollToElement('documentsCard', 205);
          }, 0);
        }
      });

      this.route.queryParams.pipe(takeUntil(this.stop$)).subscribe(queryParams => {
        if (queryParams.openAccordion) {
          this.openGeneral = false;
          this.openCoverage = false;
          this.openDiscounts = false;
          this.openDocuments = false;
          switch (queryParams.openAccordion) {
            case 'general':
              this.openGeneral = true;
              break;
            case 'coverage':
              this.openCoverage = true;
              break;
            case 'discounts':
              this.openDiscounts = true;
              break;
            case 'documents':
              this.openDocuments = true;
              break;
          }
        }
      });

      combineLatest(
        this.store.select(PolicySelectors.selectSelectedPolicy),
        this.store.select(BrandSelectors.selectIsPartner)
      )
        .pipe(takeUntil(this.stop$))
        .subscribe(res => {
          const policy = res[0];
          this.isConnectPartner = res[1];
          if (policy) {
            this.policy = policy as PropertyPolicy;
            this.showNewBadge = false;
            this.setPeopleCovered();
            this.peopleCoveredActionButtons = this.coverageActionButtons =
              this.policyUtilService.getPeopleCoveredAction();
            this.premiumActionButtons$ = this.policyUtilService
              .getPremiumActionButtons(
                this.utilService.onlyAlphanumeric(this.policy.billingAccountNumber)
              )
              .pipe(takeUntil(this.stop$));
            this.policyDocuments = this.store.pipe(
              select(PolicyDocumentsSelectors.selectPolicyDocuments),
              map(documents =>
                documents.filter(policyDocument => {
                  if (
                    (policyDocument.isNew &&
                      policyDocument.policyNumber === this.policy.policyNumber) ||
                    policyDocument.policyNumber.split('[').join('').split(']').join('') ===
                      this.policy.policyNumber
                  ) {
                    this.showNewBadge = true;
                  }
                  return (
                    policyDocument.policyNumber === this.policy.policyNumber ||
                    policyDocument.policyNumber.split('[').join('').split(']').join('') ===
                      this.policy.policyNumber
                  );
                })
              )
            );
            this.setupDockingBar();
            this.setupJumpLinks();
          }
        });

      this.store
        .select(PolicySelectors.selectLatestPolicyError)
        .pipe(filter(error => !!error))
        .pipe(takeUntil(this.stop$))
        .subscribe(error => {
          this.analyticsFacade.trackPageAndError(this.pageAnalytic, 'PolicyLoadError');
          throw error;
        });

      // Subscribe to get the Go Paperless Status
      this.store
        .select(GoPaperLessSelectors.selectIsEnrolledGoPaperless)
        .pipe(takeUntil(this.stop$))
        .subscribe(isEnrolled => {
          this.isEnrolledGoPaperless = isEnrolled;
        });

      this.impersonateRolesServiceIsAuthorizedSub$ = this.impersonateRolesService.isAuthorized(
        ImpersonationRoles.REPORT_CLAIM_VIEW
      );
      this.impersonateRolesServiceIsAuthorizedSub$.pipe(takeUntil(this.stop$)).subscribe(result => {
        this.isAuthorized = result;
        this.setupDockingBar();
      });

      this.quickLinksOverride = this.quickLinksService.buildQuickLinks();
      this.spinner.stop();
    }
    this.sendAnalytics();
  }

  private getPolicyNumber() {
    return this.route.snapshot.paramMap.get('id');
  }

  private sendAnalytics() {
    this.analyticsFacade.trackEvent({
      event: 'policy_details_review',
      product_line: 'home'
    });
    this.analyticsFacade.trackPage(this.pageAnalytic);
  }

  private async buildPolicyView() {
    try {
      this.policyNumber = this.route.snapshot.paramMap.get('id');
      this.policyView = await this.policyViewService.buildPolicyView(
        this.policyNumber,
        undefined,
        TypeOfPolicy.Home
      );
      this.keepSessionAlive();
    } catch (error) {}
  }

  private setupJumpLinks() {
    this.jumpLinks = [
      {
        iconClass: 'icon-online-billing',
        clickEvent: () => {
          this.scrollService.scrollToElement('premiumCard', 85);
          this.analyticsFacade.trackButtonClick(PolicyDetails.jumpLinkPremium);
        },
        name: 'Premium'
      },
      {
        iconUrl: 'jump-link-coverage.svg',
        clickEvent: () => {
          this.scrollService.scrollToElement('coveragesCard', 85);
          this.analyticsFacade.trackButtonClick(PolicyDetails.jumpLinkCoverage);
        },
        name: 'Coverage'
      },
      {
        iconClass: 'icon-documents',
        clickEvent: () => {
          this.scrollService.scrollToElement('documentsCard', 85);
          this.analyticsFacade.trackButtonClick(PolicyDetails.jumpLinkPolicyDetailsDocuments);
        },
        name: 'Documents'
      }
    ];
  }

  public getPolicyAddress(): string {
    if (this.policy && this.policy.riskLocationAddress) {
      return this.policy.riskLocationAddress.addressLine1;
    }
    return '';
  }

  setupDockingBar() {
    this.dockingBarService.clear();
    /* Set up the action buttons */
    const reportClaimAction: Action = new Action('File a Claim', () => {
      // Check FNOL feature is enabled.
      this.claimsFNOLEnabled = this.feature.isEnabled('fnol') && !this.isConnectPartner;

      if (this.claimsFNOLEnabled) {
        this.store.dispatch(
          fromRouterActions.Go({
            path: [ClaimsNavigationPaths.CLAIM_REPORTFNOL_HOME_PATH]
          })
        );
      } else {
        this.store.dispatch(
          fromRouterActions.Go({
            path: [ClaimsNavigationPaths.CLAIM_REPORT_HOME_PATH]
          })
        );
      }
    });
    this.dockingBarService.registerHeading('Policy Details');
    this.impersonateRolesService
      .isAuthorized(ImpersonationRoles.REPORT_CLAIM_VIEW)
      .subscribe(result => {
        if (result) {
          this.dockingBarService.registerAction(reportClaimAction);
        }
      });
  }

  onCoverageDetailsClicked() {
    this.analyticsFacade.trackButtonClick(PolicyDetails.jumpLinkCoverage);
    const isSmallScreen = this.breakpointObserver.isMatched(
      '(max-width: 480px) and (orientation: portrait)'
    );
    if (isSmallScreen) this.scrollService.scrollToElement('coveragesCard', 85);
    else this.scrollService.scrollToElement('coveragesCard', 215);
  }

  gotodocuments(elementRef: ElementRef) {
    if (elementRef) {
      const shadowRoot = elementRef.nativeElement.shadowRoot;
      if (shadowRoot) {
        const targetElement = shadowRoot.querySelector(
          'embedded-modules-policy-view-documents-card'
        );
        if (targetElement) {
          const isSmallScreen = this.breakpointObserver.isMatched(
            '(max-width: 960px) and (orientation: portrait)'
          );
          if (isSmallScreen) this.scrollService.scrollIntoView(targetElement, 85);
          else this.scrollService.scrollIntoView(targetElement, 215);
        }
      }
    }
  }

  goToFileClaim() {
    this.store
      .select(selectBrandLoaded)
      .pipe(
        filter(Boolean),
        withLatestFrom(this.store.select(selectIsPartner)),
        first(),
        tap(([, isPartner]) => {
          const claimsFNOLEnabled =
            this.feature.isEnabled('fnol') &&
            (!isPartner || this.feature.isEnabled('partner_dynamic_fnol'));
          if (claimsFNOLEnabled) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [ClaimsNavigationPaths.CLAIM_REPORTFNOL_HOME_PATH]
              })
            );
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [ClaimsNavigationPaths.CLAIM_REPORT_HOME_PATH]
              })
            );
          }
        })
      )
      .subscribe();
  }

  goToEditLender() {
    this.analyticsFacade.trackButtonClick(PolicyAnalytics.buttonPoliciesEditLenderInfo);
    this.router.navigateByUrl(`/policies/changerequest`, {
      state: {
        changeRequestType: PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE,
        selectedPolicyNumber: this.policyNumber
      }
    });
  }

  goToRequestPolicyUpdate() {
    this.analyticsFacade.trackButtonClick(PolicyDetails.buttonRequestPolicyChange);
    this.router.navigate(['/policies/changerequest'], {
      state: { changeRequestType: PolicyChangeRequestType.POLICY_COVERAGE_CHANGE }
    });
  }

  onAction(e) {
    console.log('on action', e);
    const event = e.event;
    const elementRef = e.elementRef;
    const action = event?.detail?.id || event?.detail?.name;

    if (this.inWebview && String(action).toUpperCase() !== PolicyActions.GETDOCUMENTS) {
      let eventInfo = event?.detail;
      if (eventInfo) {
        eventInfo = { ...eventInfo, policyNumber: this.policyNumber };
      }
      this.policyViewService.sendToApp(eventInfo);
      return;
    }

    switch (String(action).toUpperCase()) {
      case PolicyActions.REQUESTAPOLICYUPDATE:
        this.goToRequestPolicyUpdate();
        break;

      case PolicyActions.FILECLAIM:
        this.goToFileClaim();
        break;

      case PolicyActions.GETDOCUMENTS:
        this.gotodocuments(elementRef);
        break;

      case PolicyActions.EDITLENDERINFO:
        this.goToEditLender();
        break;
    }
  }

  private setPeopleCovered() {
    const pni = this.getNameFromPolicyRoles(PolicyRole.PRIMARYNAMEDINSURED);
    const sni = this.getNameFromPolicyRoles(PolicyRole.SECONDARYNAMEDINSURED);
    if (pni || sni) {
      const nameInsured = `${pni?.name}${sni?.name ? `, ${sni.name}` : ''}`;
      if (nameInsured) {
        this.nameInsured = nameInsured;
        this.nameInsuredHeading = `Named Insured${this.nameInsured?.includes(',') ? '(s)' : ''}`;
      }
    }
  }

  private getNameFromPolicyRoles(key: string): NamedInsured {
    if (!key || !this.policy) return null;
    const roles = this.policy.policyRoles;
    const role = roles?.find(role => role.roleType.toLowerCase() === key.toLowerCase());
    return role;
  }

  keepSessionAlive() {
    if (this.inWebview)
      this.sessionHandler = setInterval(() => {
        console.log('Home - keep session!');
        this.sessionActivityService.registerUserActivity();
      }, 60000);
  }

  ngOnDestroy() {
    if (this.sessionHandler) this.sessionHandler();
    this.stop$.next();
    this.stop$.complete();
    this.dockingBarService.clear();
  }
}
